import * as React from 'react';
import { useClient } from '../client';
import { StakeholderInformation } from '../client/types/CreateEmailObjectRequest';

export const useExistingStakeholders = (
  emails: Office.EmailAddressDetails[],
): {
  isLoading: boolean;
  matchingUsers: StakeholderInformation[];
  cmStakeholders: StakeholderInformation[];
  newStakeholders: StakeholderInformation[];
  refetch: () => void;
} => {
  const { useFetchStakeholders, useFetchUsers } = useClient();

  const uniqueAddrs = React.useMemo(() => [...new Map(emails.map((s) => [s.emailAddress.toLowerCase(), s])).values()], [emails]);
  const { isLoading, matchingRecords: matchingStakeholders, refetch } = useFetchStakeholders(uniqueAddrs);
  const { isLoading: isUsersLoading, matchingRecords: matchingUsers, refetch: refetchUsers } = useFetchUsers(uniqueAddrs);

  const getNewStakeholders = React.useCallback(() => {
    /* 
      A stakeholder is 'new' when:
        The email does not have a matching stakeholder
        The email is not attached to a user
    */
    return uniqueAddrs
      .filter((email) => !new Set(matchingStakeholders.map((x) => x.email.toLocaleLowerCase())).has(email.emailAddress.toLocaleLowerCase()))
      .filter((email) => !new Set(matchingUsers.map((x) => x.email.toLocaleLowerCase())).has(email.emailAddress.toLocaleLowerCase()))
      .map((email) => ({
        name: email.displayName,
        email: email.emailAddress,
      }));
  }, [matchingStakeholders, matchingUsers, uniqueAddrs]);

  return React.useMemo(() => {
    return {
      isLoading: isLoading || isUsersLoading,
      cmStakeholders: matchingStakeholders,
      newStakeholders: getNewStakeholders(),
      refetch: () => {
        refetch();
        refetchUsers();
      },
      matchingUsers,
    };
  }, [isLoading, isUsersLoading, matchingStakeholders, getNewStakeholders, matchingUsers, refetch, refetchUsers]);
};
